import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlass, Fire, Trophy, Star, Sparkle, ChatCircleDots,
  ChartBar, Brain, Buildings, Robot, GraduationCap, Lightning,
  Clock, CheckCircle, CaretRight, Medal,
} from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import TeenVestLoader from '../components/TeenVestLoader';
import LessonReaderModal from '../components/LessonReaderModal';
import AITutorChat from '../components/AITutorChat';
import api from '../lib/api';
import { toast } from 'sonner';

const CATEGORY_META = {
  'Basics':             { icon: GraduationCap, color: 'from-blue-400 to-blue-600',       accent: 'blue' },
  'Investing 101':      { icon: ChartBar,      color: 'from-emerald-400 to-emerald-600', accent: 'emerald' },
  'Markets':            { icon: Buildings,     color: 'from-amber-400 to-amber-600',     accent: 'amber' },
  'Psychology':         { icon: Brain,         color: 'from-fuchsia-400 to-fuchsia-600', accent: 'fuchsia' },
  'AI & Modern Finance':{ icon: Robot,         color: 'from-purple-400 to-purple-600',   accent: 'purple' },
  'Advanced':           { icon: Lightning,     color: 'from-red-400 to-red-600',         accent: 'red' },
};

const LEVEL_META = {
  beginner:     { label: 'Beginner',     color: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' },
  intermediate: { label: 'Intermediate', color: 'bg-amber-500/10 text-amber-300 border-amber-500/30' },
  advanced:     { label: 'Advanced',     color: 'bg-red-500/10 text-red-300 border-red-500/30' },
};

const BADGE_META = {
  first_lesson: { emoji: '🌱', label: 'First Steps' },
  apprentice:   { emoji: '📚', label: 'Apprentice' },
  scholar:      { emoji: '🎓', label: 'Scholar' },
  expert:       { emoji: '🧠', label: 'Expert' },
  master:       { emoji: '👑', label: 'Master' },
};

const Learn = () => {
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [progress, setProgress] = useState({ completed_ids: [], xp_earned: 0, streak_days: 0, badges: [] });
  const [activeCategory, setActiveCategory] = useState('All');
  const [query, setQuery] = useState('');
  const [openLesson, setOpenLesson] = useState(null);
  const [showTutor, setShowTutor] = useState(false);
  const [tutorLessonId, setTutorLessonId] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/learn/v2/lessons');
      setLessons(res.data.lessons);
      setCategories(res.data.categories);
      setProgress(res.data.progress);
    } catch (e) {
      toast.error('Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const completedSet = useMemo(() => new Set(progress.completed_ids), [progress.completed_ids]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return lessons.filter((l) => {
      if (activeCategory !== 'All' && l.category !== activeCategory) return false;
      if (!q) return true;
      return (l.title + ' ' + l.overview + ' ' + l.category).toLowerCase().includes(q);
    });
  }, [lessons, activeCategory, query]);

  const nextLesson = useMemo(() => lessons.find((l) => !completedSet.has(l.id)), [lessons, completedSet]);

  const totalXP = progress.xp_earned;
  const level = 1 + Math.floor(totalXP / 200);
  const xpIntoLevel = totalXP % 200;
  const xpToNext = 200 - xpIntoLevel;

  const openLessonById = async (id) => {
    try {
      const res = await api.get(`/learn/v2/lesson/${id}`);
      setOpenLesson(res.data.lesson);
    } catch (e) {
      toast.error('Failed to open lesson');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <TeenVestLoader size={112} label="Loading your curriculum..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen noise-bg" data-testid="learn-page">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 py-8 relative">
        {/* ==================== HEADER ==================== */}
        <div className="mb-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Learn</p>
          <h1 className="text-4xl md:text-5xl font-unbounded font-bold text-white mb-2">
            Level up your investing brain.
          </h1>
          <p className="text-zinc-400">40 lessons · 6 categories · earn XP, streaks and badges as you go.</p>
        </div>

        {/* ==================== TOP STAT CARDS ==================== */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="glass-card p-4" data-testid="learn-stat-level">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1"><Trophy size={12} className="text-gold" /> Investor Level</p>
            <p className="text-2xl font-mono font-bold text-white">L{level}</p>
            <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-yellow-300 to-yellow-500" style={{ width: `${(xpIntoLevel / 200) * 100}%` }} />
            </div>
            <p className="text-[10px] text-zinc-500 mt-1">{xpToNext} XP to L{level + 1}</p>
          </div>

          <div className="glass-card p-4" data-testid="learn-stat-xp">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1"><Sparkle size={12} weight="fill" className="text-gold" /> Total XP</p>
            <p className="text-2xl font-mono font-bold text-gold">{totalXP.toLocaleString()}</p>
            <p className="text-[10px] text-zinc-500 mt-1">{progress.completed_ids.length} / {lessons.length} lessons</p>
          </div>

          <div className="glass-card p-4" data-testid="learn-stat-streak">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1"><Fire size={12} weight="fill" className="text-orange-400" /> Streak</p>
            <p className="text-2xl font-mono font-bold text-orange-300">{progress.streak_days}<span className="text-sm text-zinc-500 ml-1">days</span></p>
            <p className="text-[10px] text-zinc-500 mt-1">Complete a lesson today to keep it going</p>
          </div>

          <div className="glass-card p-4" data-testid="learn-stat-badges">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1"><Medal size={12} weight="fill" className="text-fuchsia-400" /> Badges</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {Object.entries(BADGE_META).map(([k, meta]) => {
                const earned = (progress.badges || []).includes(k);
                return (
                  <span
                    key={k}
                    title={meta.label}
                    className={`text-lg ${earned ? '' : 'grayscale opacity-30'}`}
                  >{meta.emoji}</span>
                );
              })}
            </div>
            <p className="text-[10px] text-zinc-500 mt-1">{(progress.badges || []).length} of {Object.keys(BADGE_META).length}</p>
          </div>
        </div>

        {/* ==================== CONTINUE LEARNING BANNER ==================== */}
        {nextLesson && (
          <motion.button
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            onClick={() => openLessonById(nextLesson.id)}
            className="w-full text-left group relative overflow-hidden rounded-3xl bg-gradient-to-r from-yellow-500/20 via-yellow-400/10 to-transparent border border-gold/30 p-6 md:p-8 mb-8 hover:border-gold/60 transition-colors"
            data-testid="learn-continue-banner"
          >
            <div className="flex items-center gap-6">
              <div className="flex-shrink-0 text-6xl md:text-7xl">{nextLesson.emoji}</div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-gold mb-1">Continue learning</p>
                <h3 className="text-white font-unbounded font-bold text-xl md:text-2xl leading-tight mb-1">{nextLesson.title}</h3>
                <p className="text-sm text-zinc-400 line-clamp-2">{nextLesson.overview}</p>
                <div className="flex gap-3 mt-3 text-xs text-zinc-500">
                  <span className="flex items-center gap-1"><Clock size={12} /> {nextLesson.reading_minutes} min</span>
                  <span className="flex items-center gap-1"><Sparkle size={12} weight="fill" className="text-gold" /> +{nextLesson.xp_reward} XP</span>
                </div>
              </div>
              <CaretRight size={24} weight="bold" className="text-gold group-hover:translate-x-1 transition-transform hidden md:block" />
            </div>
          </motion.button>
        )}

        {/* ==================== SEARCH + CATEGORY TABS ==================== */}
        <div className="mb-6 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search lessons, topics, keywords..."
              className="w-full bg-white/5 border border-white/10 rounded-full pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-gold/50"
              data-testid="learn-search-input"
            />
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-3 mb-6 -mx-6 px-6" data-testid="learn-category-tabs">
          {['All', ...categories].map((cat) => {
            const meta = CATEGORY_META[cat];
            const Icon = meta?.icon || GraduationCap;
            const active = activeCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                  active
                    ? 'bg-gold text-black border-gold'
                    : 'bg-white/5 text-zinc-300 border-white/10 hover:border-white/30'
                }`}
                data-testid={`learn-tab-${cat.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                {cat !== 'All' && <Icon size={14} weight={active ? 'fill' : 'regular'} />}
                {cat}
              </button>
            );
          })}
        </div>

        {/* ==================== LESSON GRID ==================== */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="learn-lesson-grid">
          {filtered.map((l, i) => {
            const meta = CATEGORY_META[l.category] || {};
            const Icon = meta.icon || GraduationCap;
            const done = completedSet.has(l.id);
            const levelMeta = LEVEL_META[l.level] || LEVEL_META.beginner;
            return (
              <motion.button
                key={l.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ delay: (i % 12) * 0.03 }}
                whileHover={{ y: -3 }}
                onClick={() => openLessonById(l.id)}
                className="text-left glass-card p-5 group hover:border-gold/40 transition-colors relative"
                data-testid={`learn-lesson-card-${l.id}`}
              >
                {done && (
                  <span className="absolute top-3 right-3">
                    <CheckCircle size={18} weight="fill" className="text-emerald-400" />
                  </span>
                )}

                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.color || 'from-zinc-600 to-zinc-800'} flex items-center justify-center mb-3 text-2xl group-hover:scale-110 transition-transform`}>
                  <span>{l.emoji}</span>
                </div>

                <div className="flex items-center gap-1.5 mb-2">
                  <span className={`text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${levelMeta.color}`}>
                    {levelMeta.label}
                  </span>
                  <span className="flex items-center gap-0.5 text-[10px] text-zinc-500">
                    <Clock size={10} /> {l.reading_minutes}m
                  </span>
                </div>

                <h3 className="text-white font-unbounded font-bold text-base leading-tight mb-1.5 group-hover:text-gold transition-colors">
                  {l.title}
                </h3>
                <p className="text-xs text-zinc-400 line-clamp-2 mb-3">{l.overview}</p>

                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                    <Icon size={11} weight="fill" /> {l.category}
                  </span>
                  <span className="flex items-center gap-0.5 text-xs font-mono font-bold text-gold">
                    <Sparkle size={10} weight="fill" /> +{l.xp_reward}
                  </span>
                </div>

                {/* progress bar sliver */}
                <div className="mt-3 h-1 bg-white/5 rounded-full overflow-hidden">
                  <div className={`h-full transition-all ${done ? 'bg-emerald-400 w-full' : 'bg-gold/30 w-0'}`} />
                </div>
              </motion.button>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-zinc-500">
            No lessons match your search.
          </div>
        )}

        {/* ==================== AI TUTOR FAB ==================== */}
        <button
          onClick={() => { setTutorLessonId(null); setShowTutor(true); }}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-br from-purple-400 to-cyan-400 text-black font-bold text-sm shadow-[0_10px_30px_-5px_rgba(168,85,247,0.5)] hover:scale-105 transition-transform"
          data-testid="learn-ai-tutor-fab"
        >
          <ChatCircleDots size={18} weight="fill" /> AI Tutor
        </button>
      </div>

      <AnimatePresence>
        {openLesson && (
          <LessonReaderModal
            lesson={openLesson}
            onClose={() => setOpenLesson(null)}
            onComplete={() => load()}
            onAskTutor={(id) => { setTutorLessonId(id); setShowTutor(true); }}
          />
        )}
        {showTutor && (
          <AITutorChat lessonId={tutorLessonId} onClose={() => setShowTutor(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Learn;
